import { useState, useEffect, useRef, useCallback } from 'react'
import useAvailabilityStore from '@/stores/availability.store'

/**
 * WebSocket message type for bed availability changes.
 */
export interface BedChangeMessage {
  type: 'bed_change'
  service: string
  available: number
  occupied: number
  reserved: number
  timestamp: string
}

type ConnectionStatus = 'connected' | 'disconnected' | 'reconnecting'

interface UseWebSocketReturn {
  isConnected: boolean
  lastMessage: BedChangeMessage | null
  reconnectAttempts: number
  connectionStatus: ConnectionStatus
}

const MAX_RECONNECT_DELAY_MS = 30_000
const BASE_RECONNECT_DELAY_MS = 1_000

function isBedChangeMessage(data: unknown): data is BedChangeMessage {
  if (typeof data !== 'object' || data === null) return false
  const msg = data as Record<string, unknown>
  return (
    msg.type === 'bed_change' &&
    typeof msg.service === 'string' &&
    typeof msg.available === 'number' &&
    typeof msg.occupied === 'number' &&
    typeof msg.reserved === 'number' &&
    typeof msg.timestamp === 'string'
  )
}

function getWebSocketUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const host = window.location.host
  const base = import.meta.env.BASE_URL.replace(/\/$/, '')
  return `${protocol}//${host}${base}/ws/bed-changes`
}

/**
 * Hook that connects to the bed-changes WebSocket endpoint.
 *
 * - Auto-reconnects with exponential backoff (1s, 2s, 4s, 8s, ... max 30s)
 * - Parses `bed_change` JSON messages and updates the Zustand availability store
 * - Skips connection when VITE_MOCK_API is "true"
 */
export function useWebSocket(): UseWebSocketReturn {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected')
  const [lastMessage, setLastMessage] = useState<BedChangeMessage | null>(null)
  const [reconnectAttempts, setReconnectAttempts] = useState(0)

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const attemptsRef = useRef(0)
  const isMountedRef = useRef(true)

  const updateBed = useAvailabilityStore((s) => s.updateBed)

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
  }, [])

  const connect = useCallback(() => {
    // Skip if mock API is active
    if (import.meta.env.VITE_MOCK_API === 'true') return
    if (!isMountedRef.current) return

    // Close any existing connection
    if (wsRef.current) {
      wsRef.current.onopen = null
      wsRef.current.onclose = null
      wsRef.current.onerror = null
      wsRef.current.onmessage = null
      if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
        wsRef.current.close()
      }
      wsRef.current = null
    }

    const url = getWebSocketUrl()
    const ws = new WebSocket(url)
    wsRef.current = ws

    ws.onopen = () => {
      if (!isMountedRef.current) return
      attemptsRef.current = 0
      setReconnectAttempts(0)
      setConnectionStatus('connected')
    }

    ws.onmessage = (event: MessageEvent) => {
      if (!isMountedRef.current) return
      try {
        const data: unknown = JSON.parse(String(event.data))
        if (isBedChangeMessage(data)) {
          setLastMessage(data)
          // Update the availability store with new bed counts
          updateBed(data.service, 'available', data.available)
          updateBed(data.service, 'occupied', data.occupied)
          updateBed(data.service, 'reserved', data.reserved)
        }
      } catch {
        // Ignore non-JSON or malformed messages
      }
    }

    ws.onclose = () => {
      if (!isMountedRef.current) return
      scheduleReconnect()
    }

    ws.onerror = () => {
      // onclose will fire after onerror, so reconnect is handled there
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateBed, clearReconnectTimer])

  const scheduleReconnect = useCallback(() => {
    if (!isMountedRef.current) return
    clearReconnectTimer()

    attemptsRef.current += 1
    setReconnectAttempts(attemptsRef.current)
    setConnectionStatus('reconnecting')

    const delay = Math.min(
      BASE_RECONNECT_DELAY_MS * Math.pow(2, attemptsRef.current - 1),
      MAX_RECONNECT_DELAY_MS
    )

    reconnectTimerRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        connect()
      }
    }, delay)
  }, [connect, clearReconnectTimer])

  useEffect(() => {
    isMountedRef.current = true

    // Do not connect if mock mode is active
    if (import.meta.env.VITE_MOCK_API === 'true') return

    connect()

    return () => {
      isMountedRef.current = false
      clearReconnectTimer()
      if (wsRef.current) {
        wsRef.current.onopen = null
        wsRef.current.onclose = null
        wsRef.current.onerror = null
        wsRef.current.onmessage = null
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [connect, clearReconnectTimer])

  return {
    isConnected: connectionStatus === 'connected',
    lastMessage,
    reconnectAttempts,
    connectionStatus,
  }
}
